import { createApi } from "@reduxjs/toolkit/query/react";
import { baseQuery } from "./utils";
import type { GetDocumentAnalysisViewResponse } from "../../../api/src/documents/llm/view";
import {
  LlmAnalysisItemStatus,
  LlmAttorneyView,
  type LlmAnalysis,
  type LlmEditorSuggestion,
} from "../../../api/src/documents/llm/shared";
import type {
  ResolveItemAction,
  ResolveLlmAnalysisItemResponse,
} from "../../../api/src/documents/llm/items";

function setAnalysisItemStatus(
  analysis: LlmAnalysis,
  itemId: string,
  status: LlmAnalysisItemStatus,
): void {
  if (analysis.summary?.id === itemId) {
    analysis.summary.status = status;
    return;
  }

  const item = [
    ...analysis.editorSuggestions,
    ...analysis.agreementRisks,
    ...analysis.attorneyInsights,
  ].find((entry) => entry.id === itemId);

  if (item) {
    item.status = status;
  }
}

type OptimisticEditorSuggestionDerivedFields = Pick<
  LlmEditorSuggestion,
  "interactionMode" | "note"
>;

function applyEditorSuggestionDerivedFields(
  analysis: LlmAnalysis,
  itemId: string,
  fields: OptimisticEditorSuggestionDerivedFields,
): void {
  const suggestion = analysis.editorSuggestions.find((s) => s.id === itemId);
  if (!suggestion) return;
  suggestion.interactionMode = fields.interactionMode;
  suggestion.note = fields.note;
}

export const documentAnalysisApi = createApi({
  reducerPath: "documentAnalysisApi",
  baseQuery,
  endpoints: (builder) => ({
    getDocumentAnalysisView: builder.query<
      GetDocumentAnalysisViewResponse,
      { docId: string; attorneyView: LlmAttorneyView }
    >({
      query: ({ docId, attorneyView }) =>
        `documents/${docId}/analysis-view?attorneyView=${attorneyView}`,
    }),
    resolveLlmAnalysisItem: builder.mutation<
      ResolveLlmAnalysisItemResponse,
      {
        docId: string;
        attorneyView: LlmAttorneyView;
        itemId: string;
        action: ResolveItemAction;
        optimisticStatus: LlmAnalysisItemStatus;
        // For "restore" of an editor suggestion: derived fields recomputed
        // locally from live clause text (server response doesn't carry the
        // recomputed item and a refetch would read stale DB text — SyncPlugin
        // debounces saves). Computed via computeEditorSuggestionDerivedFields
        // in shared.ts.
        optimisticEditorSuggestionDerivedFields?: OptimisticEditorSuggestionDerivedFields;
      }
    >({
      query: ({ docId, itemId, action, attorneyView }) => ({
        url: `documents/${docId}/llm-analysis-items/${itemId}`,
        method: "POST",
        params: { action, attorneyView },
      }),
      async onQueryStarted(
        {
          docId,
          attorneyView,
          itemId,
          optimisticStatus,
          optimisticEditorSuggestionDerivedFields,
        },
        { dispatch, queryFulfilled },
      ) {
        const queryArgs = { docId, attorneyView };
        const patch = dispatch(
          documentAnalysisApi.util.updateQueryData(
            "getDocumentAnalysisView",
            queryArgs,
            (draft) => {
              if (draft.analysis) {
                setAnalysisItemStatus(draft.analysis, itemId, optimisticStatus);
                if (optimisticEditorSuggestionDerivedFields) {
                  applyEditorSuggestionDerivedFields(
                    draft.analysis,
                    itemId,
                    optimisticEditorSuggestionDerivedFields,
                  );
                }
              }
            },
          ),
        );

        try {
          await queryFulfilled;
        } catch (error) {
          patch.undo();
          void dispatch(
            documentAnalysisApi.endpoints.getDocumentAnalysisView.initiate(
              queryArgs,
              { forceRefetch: true },
            ),
          );
          console.error("Failed to resolve LLM analysis item", error);
        }
      },
    }),
  }),
});

export const {
  useGetDocumentAnalysisViewQuery,
  useResolveLlmAnalysisItemMutation,
} = documentAnalysisApi;
